import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { backendUrl } from "../utils/urlHelper";

const ManageUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [branches, setBranches] = useState([]);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchUsers();
    fetchBranches();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(backendUrl + "/api/users/", {
        headers: { Authorization: token },
      });
      setUsers(response.data);
    } catch (err) {
      console.error("Failed to fetch users");
    }
  };

  const handleToggleActive = async (user) => {
    try {
      setLoading(true);
      setError("");
      await axios.put(
        backendUrl + `/api/users/${user._id || user.id}`,
        { isActive: !user.isActive },
        { headers: { Authorization: token } }
      );
      setLoading(false);
      fetchUsers();
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || "Failed to update user status");
    }
  };

  const handleUpdateUser = async () => {
    try {
      setLoading(true);
      const {
        _id,
        username,
        name,
        branch,
        role,
        isActive,
        password,
        secondaryBranches,
      } = editingUser;
      await axios.put(
        backendUrl + `/api/users/${_id}`,
        { username, name, branch, role, isActive, password, secondaryBranches },
        {
          headers: { Authorization: token },
        }
      );
      setLoading(false);
      setError("User details updated");
      fetchUsers(); // Refresh user list
      setEditingUser(null);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || "Failed to update user");
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(backendUrl + "/api/list/branches/");
      setBranches(response?.data?.branches);
    } catch (err) {
      console.error("Failed to fetch users");
    }
  };

  // Filtered users based on search query
  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username.includes(searchQuery)
  );

  // Handle Edit
  const handleEditClick = (user) => {
    setEditingUser({ ...user });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ textAlign: "center", mb: 4 }}>
        Manage Users
      </Typography>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search by Name or Phone Number"
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      {error && (
        <Typography sx={{ my: 1 }} color="error">
          {error}
        </Typography>
      )}

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>Phone Number</strong>
              </TableCell>
              <TableCell>
                <strong>Branch</strong>
              </TableCell>
              <TableCell>
                <strong>Role</strong>
              </TableCell>
              <TableCell>
                <strong>Status</strong>
              </TableCell>
              <TableCell>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.branch}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>
                  <Chip
                    label={user.isActive ? "Active" : "Inactive"}
                    color={user.isActive ? "success" : "error"}
                    size="small"
                    variant="contained"
                    sx={{
                      fontWeight: 600,
                      minWidth: 80,
                      justifyContent: "center",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={user.isActive ? "Deactivate user" : "Activate user"}
                  >
                    <span>
                      <IconButton
                        onClick={() => handleToggleActive(user)}
                        disabled={loading}
                      >
                        {user.isActive ? (
                          <RemoveCircleOutlineIcon />
                        ) : (
                          <AddCircleOutlineIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Edit user">
                    <span>
                      <IconButton
                        onClick={() => handleEditClick(user)}
                        disabled={loading}
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button
        variant="outlined"
        fullWidth
        sx={{
          backgroundColor: "#eeeeee",
          padding: "0.75rem",
          textTransform: "none",
          my: 2,
        }}
        onClick={() => navigate(-1)}
      >
        Go Back
      </Button>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onClose={() => setEditingUser(null)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 1,
            }}
          >
            <TextField
              label="Name"
              variant="outlined"
              name="name"
              value={editingUser?.name || ""}
              onChange={(e) =>
                setEditingUser((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              fullWidth
            />

            <TextField
              label="Phone Number"
              variant="outlined"
              name="username"
              value={editingUser?.username || ""}
              onChange={(e) =>
                setEditingUser((prev) => ({
                  ...prev,
                  username: e.target.value,
                }))
              }
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Branch</InputLabel>
              <Select
                label="Branch"
                value={editingUser?.branch || ""}
                onChange={(e) =>
                  setEditingUser((prev) => ({
                    ...prev,
                    branch: e.target.value,
                  }))
                }
              >
                {branches.map((branch, index) => (
                  <MenuItem key={index} value={branch}>
                    {branch}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={editingUser?.role || ""}
                onChange={(e) =>
                  setEditingUser((prev) => ({
                    ...prev,
                    role: e.target.value,
                  }))
                }
              >
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            {/* Secondary Branches */}
            {editingUser?.role === "manager" && (
              <FormControl fullWidth>
                <InputLabel>Secondary Branches</InputLabel>
                <Select
                  label="Secondary Branches"
                  multiple
                  value={editingUser?.secondaryBranches || []}
                  name="secondaryBranches"
                  onChange={(e) =>
                    setEditingUser((prev) => ({
                      ...prev,
                      secondaryBranches: e.target.value,
                    }))
                  }
                  renderValue={(selected) => selected.join(", ")} // Display selected branches
                >
                  {branches.map((branch, index) => (
                    <MenuItem
                      key={index}
                      value={branch}
                      sx={{
                        backgroundColor:
                          editingUser?.secondaryBranches.includes(branch)
                            ? "rgba(0, 0, 255, 0.1)" // Light blue background for selected items
                            : "inherit",
                        "&.Mui-selected": {
                          backgroundColor: "rgba(0, 0, 255, 0.2) !important", // Slightly darker blue when selected
                          color: "#000", // Ensure text is black for better readability
                        },
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 255, 0.2)", // Hover effect
                        },
                      }}
                    >
                      {branch}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Password"
              variant="outlined"
              type="password"
              name="password"
              value={editingUser?.password || ""}
              onChange={(e) =>
                setEditingUser((prev) => ({
                  ...prev,
                  password: e.target.value,
                }))
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingUser(null)}>Cancel</Button>
          <Button onClick={handleUpdateUser} color="primary" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageUsersPage;
